import { Button } from '@timelyapp/react';
import { Plug } from 'components/Icons';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import type { FactoryApp } from 'factories/apps';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import { useDispatch } from 'react-redux';
import { enableIntegration } from 'redux/modules/integrations.actions';
import type { ThunkDispatch } from 'types/redux';
import styles from './IntegrationConnectMessage.module.scss';

interface ConnectButtonProps {
  onClick?: () => void;
  icon?: string;
  title: string;
  showCaret?: boolean;
}

const ConnectButton: React.FC<ConnectButtonProps> = ({ onClick, icon, title, showCaret }) => {
  return (
    <Button priority="low" onClick={onClick} className={styles.dropdownContainer}>
      {icon ? (
        <div className={styles.icon}>
          <img alt={title} src={icon} width={20} />
        </div>
      ) : null}
      <div className={styles.title}>{title}</div>
      {showCaret ? (
        <div className={styles.caret}>
          <FontAwesome name="caret-down" />
        </div>
      ) : null}
    </Button>
  );
};

const IntegrationConnectMessage: React.FC<{ apps: Array<FactoryApp> }> = ({ apps }) => {
  const dispatch = useDispatch<ThunkDispatch>();

  if (!apps || !apps.length) {
    return null;
  }

  function renderOptions() {
    if (!apps) {
      return null;
    }
    if (apps.length > 1) {
      return (
        <PopoverDropdownList
          target={<ConnectButton title="Connect to..." />}
          options={apps.map(app => ({
            idRef: app.appId,
            name: app.displayName,
            icon: <img alt={app.provider} src={app.logoUrl} />,
          }))}
          onClick={option => {
            dispatch(enableIntegration(option.idRef, {}));
          }}
        />
      );
    }

    const app = apps[0];

    return (
      <ConnectButton
        title={`Connect to ${app.displayName}`}
        icon={app.logoUrl}
        onClick={() => dispatch(enableIntegration(app.appId, {}))}
      />
    );
  }

  return (
    <div className={styles.connectBanner}>
      <Plug className={styles.integrationsIcon} />
      <div className={styles.text}>
        <h1 className={styles.title}>Streamline client invoicing</h1>
        <p>
          Connect your favorite accounting apps to create accurate invoices with minimal effort.
          Timely will send invoices for all unbilled project hours to your chosen platform in a
          click.
        </p>
        <a
          target="_blank"
          rel="noopener"
          href="https://support.memory.ai/en/articles/4997622-invoicing-with-quickbooks-online"
        >
          Learn more about our invoicing integrations
        </a>
      </div>
      <div className={styles.options}>{renderOptions()}</div>
    </div>
  );
};

export default IntegrationConnectMessage;
